import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip,
  Card,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  BusinessCenter,
  CalendarToday,
  Security,
  CheckCircle,
  Warning,
  Cancel,
  Business,
  MonetizationOn,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  AccountBalance,
  Download,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchUserDetail, 
  clearSelectedUser, 
  blockUser, 
  unblockUser, 
  fetchUserActivities, 
  approveCorporateUser,
  fetchEntrepreneurForm,
  updateEntrepreneurForm,
  updateUserPassword,
  loginAsUser
} from '../store/slices/adminSlice';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import SingleFileUpload from '../components/fileUpload/FileUpload';

// Import JSON data
import cities from '../assets/json/cities.json';
import districts from '../assets/json/districts.json';
import banks from '../assets/json/banks.json';

const UserDetailPage = () => {
  const theme = useTheme();
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedUser: user, userActivities, entrepreneurForm, loading } = useSelector((state) => state.admin);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isEditingForm, setIsEditingForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [cvPath, setCvPath] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const { adminInfo } = useSelector((state) => state.auth);

  // Filter districts based on selected city
  useEffect(() => {
    if (formData.city) {
      const selectedCity = cities.find(city => city.name === formData.city);
      if (selectedCity) {
        const cityDistricts = districts.filter(district => district.il_id === selectedCity.id);
        setAvailableDistricts(cityDistricts);
      }
    }
  }, [formData.city]);

  useEffect(() => {
    dispatch(fetchUserDetail(userId));
    dispatch(fetchEntrepreneurForm(userId));

    return () => {
      dispatch(clearSelectedUser());
    };
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserActivities(userId, page + 1, rowsPerPage));
    }
  }, [dispatch, userId, page, rowsPerPage]);

  useEffect(() => {
    if (entrepreneurForm) {
      setFormData(entrepreneurForm);
      setCvPath(entrepreneurForm.cvFilePath || '');
    }
  }, [entrepreneurForm]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLoginAsUser = async () => {
    try {
      const result = await dispatch(loginAsUser(userId)).unwrap();
      if (result) {
        // Redirect to user dashboard or home page
        console.log(result)
        if (result?.token) {
          var url = "https://kf.ardventure.com.tr?token=" + result.token;
          window.open(url, '_blank');
          toast.success('Kullanıcı olarak giriş yapıldı');
        }
      }
    } catch (error) {
      toast.error('Kullanıcı olarak giriş yapılamadı: ' + (error.message || 'Bilinmeyen hata'));
    }
  }

  const handleUpdateForm = async () => {
    try {
      const { userId: _, ...restFormData } = formData;  // Remove userId from formData if exists
      const dataToUpdate = {
        ...restFormData,
        cvFilePath: cvPath || (entrepreneurForm?.cvFilePath || '')
      };
      console.log('Updating form with data:', dataToUpdate); // Debug log
      const result = await dispatch(updateEntrepreneurForm({ userId, formData: dataToUpdate })).unwrap();
      if (result) {
        toast.success('Girişimci formu başarıyla güncellendi');
        setIsEditingForm(false);
        dispatch(fetchEntrepreneurForm(userId));
      }
    } catch (error) {
      toast.error(error.message || 'Güncelleme sırasında bir hata oluştu');
    }
  };

  const handleFileUpload = (path) => {
    console.log('File upload path:', path); // Debug log
    if (path) {
      setCvPath(path);
      setFormData(prev => ({
        ...prev,
        cvFilePath: path
      }));
    }
  };

  const handleUpdatePassword = async () => {
    try {
      if (password !== confirmPassword) {
        toast.error('Şifreler eşleşmiyor');
        return;
      }
      if (password.length < 6) {
        toast.error('Şifre en az 6 karakter olmalıdır');
        return;
      }
      const result = await dispatch(updateUserPassword({ userId, password })).unwrap();
      if (result) {
        toast.success('Kullanıcı şifresi başarıyla güncellendi');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      toast.error(error.message || 'Şifre güncellenirken bir hata oluştu');
    }
  };

  if (loading) return <LinearProgress />;
  if (!user) return <Typography color="error">Kullanıcı bulunamadı</Typography>;

  const infoCards = [
    { icon: <Email />, label: 'Email', value: user.email },
    { icon: <Phone />, label: 'Telefon', value: `+${user.userDetails?.countryCode} ${user.userDetails?.phone}` },
    { icon: <BusinessCenter />, label: 'Hesap Tipi', value: user.userDetails?.userType === 'individual' ? 'Bireysel' : 'Kurumsal' },
    ...(user.userDetails?.userType === 'individual' 
      ? [
          { icon: <Person />, label: 'Ad Soyad', value: `${user.individualUser?.name} ${user.individualUser?.lastName}` },
          { icon: <CalendarToday />, label: 'Doğum Tarihi', value: dayjs(user.individualUser?.birthDate).format('DD.MM.YYYY') },
        ]
      : [
          { icon: <Business />, label: 'Şirket Adı', value: user.corporateUser?.title },
        ]
    ),
    { icon: <CalendarToday />, label: 'Kayıt Tarihi', value: dayjs(Number(user.registerTime)).format('DD.MM.YYYY HH:mm') },
    ...(user.userDetails?.userType === 'corporate' 
      ? [{ icon: <AccountBalance />, label: 'Vergi Dairesi', value: user.corporateUser?.taxOffice }]
      : []
    ),
  ];

  const getVerificationStatus = () => {
    const isEmailVerified = user.emailVerification;
    const isPhoneVerified = user.userDetails?.phoneVerification;
    const isEDevletVerified = user.individualUser?.eDevletApproved;
    const isCorporate = user.userDetails?.userType === 'corporate';
    const isCorporateApproved = user.corporateUser?.approved;

    let status = 'notSubmitted';
    if (isEmailVerified && isPhoneVerified) {
      if (isCorporate) {
        status = isCorporateApproved ? 'verified' : 'pending';
      } else {
        status = isEDevletVerified ? 'verified' : 'pending';
      }
    }

    const statusConfig = {
      verified: {
        label: isCorporate ? 'Onaylandı' : 'Doğrulandı',
        icon: <CheckCircle sx={{ fontSize: 24 }} />,
        color: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.1),
      },
      pending: {
        label: isCorporate ? 'Onay Bekliyor' : 'Doğrulama Bekleniyor',
        icon: <Warning sx={{ fontSize: 24 }} />,
        color: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.1),
      },
      notSubmitted: {
        label: 'Gönderilmedi',
        icon: <Cancel sx={{ fontSize: 24 }} />,
        color: theme.palette.grey[500],
        bgcolor: alpha(theme.palette.grey[500], 0.1),
      }
    };

    const config = statusConfig[status];

    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: config.bgcolor,
          }}
        >
          {config.icon}
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: config.color,
                fontWeight: 'medium'
              }}
            >
              {config.label}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: alpha(config.color, 0.8)
              }}
            >
              {isCorporate 
                ? status === 'verified'
                  ? 'Yetkili tarafından onaylandı'
                  : 'Yetkili onayı bekliyor'
                : status === 'verified' 
                  ? 'E-Devlet ile doğrulandı' 
                  : status === 'pending' 
                    ? 'E-Devlet doğrulaması bekleniyor' 
                    : 'E-Devlet doğrulaması başlatılmadı'}
            </Typography>
          </Box>
        </Box>
        {isCorporate && status === 'pending' && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircle />}
            onClick={handleApproveCorporateUser}
            sx={{
              bgcolor: theme.palette.success.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.success.dark,
              },
            }}
          >
            Kurumsal Kullanıcıyı Onayla
          </Button>
        )}
      </Stack>
    );
  };

  const getUserTypeChip = () => {
    let label = 'Kullanıcı';
    let color = 'info';

    if (user.userDetails?.entrepreneur && user.userDetails?.investor) {
      label = 'Girişimci & Yatırımcı';
      color = 'success';
    } else if (user.userDetails?.entrepreneur) {
      label = 'Girişimci';
      color = 'warning';
    } else if (user.userDetails?.investor) {
      label = 'Yatırımcı';
      color = 'primary';
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const handleBlockUnblock = async () => {
    if (user.enabled) {
      await dispatch(blockUser(userId));
    } else {
      await dispatch(unblockUser(userId));
    }
    setConfirmDialogOpen(false);
  };

  const handleApproveCorporateUser = async () => {
    try {
      await dispatch(approveCorporateUser(userId)).unwrap();
      toast.success('Kurumsal kullanıcı başarıyla onaylandı');
    } catch (error) {
      toast.error(error.message || 'Kurumsal kullanıcı onaylanırken bir hata oluştu');
    }
  };

  const verificationCard = (
    <Stack spacing={2}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Doğrulama Durumu</Typography>
        {getVerificationStatus()}
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Business />}
            onClick={() => navigate(`/startuplar/${userId}`)}
            sx={{
              py: 1.5,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.24)}`,
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`,
            }}
          >
            Startuplar
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<MonetizationOn />}
            onClick={() => navigate(`/yatirimlar/${userId}`)}
            sx={{
              py: 1.5,
              bgcolor: theme.palette.success.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.success.dark,
                boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.24)}`,
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.16)}`,
            }}
          >
            Yatırımlar
          </Button>
        </Grid>
      </Grid>
      {adminInfo?.type === 'SUPER_ADMIN' && (
        <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Business />}
            onClick={handleLoginAsUser}
            sx={{
              py: 1.5,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.24)}`,
              },
              transition: 'all 0.2s ease-in-out',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`,
            }}
          >
            Kullanıcı olarak giriş yap
          </Button>
        </Grid>
      </Grid>
      )}
    </Stack>
  );

  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    event.preventDefault();
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (timestamp) => {
    return new Date(parseInt(timestamp)).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateBrowser = (browser) => {
    if (!browser) return '';
    const match = browser.match(/^[^/]+\/[^/]+/);
    return match ? match[0] : browser;
  };

  const userInfoSection = (
    <Grid container spacing={3}>
      {infoCards.map((info, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              {info.icon}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {info.label}
                </Typography>
                <Typography variant="body1">
                  {info.value}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const entrepreneurFormSection = (
    <Card sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Girişimci Kayıt Formu</Typography>
        {entrepreneurForm && (
          <Button
            variant="contained"
            onClick={() => {
              if (isEditingForm) {
                handleUpdateForm();
              } else {
                setIsEditingForm(true);
              }
            }}
            sx={{
              bgcolor: isEditingForm ? theme.palette.success.main : theme.palette.primary.main,
              '&:hover': {
                bgcolor: isEditingForm ? theme.palette.success.dark : theme.palette.primary.dark,
              }
            }}
          >
            {isEditingForm ? 'Kaydet' : 'Düzenle'}
          </Button>
        )}
      </Box>
      
      {entrepreneurForm ? (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Şehir"
              value={formData.city || ''}
              onChange={(e) => handleFormChange('city', e.target.value)}
              disabled={!isEditingForm}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.name}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="İlçe"
              value={formData.district || ''}
              onChange={(e) => handleFormChange('district', e.target.value)}
              disabled={!isEditingForm || !formData.city}
            >
              {availableDistricts.map((district) => (
                <MenuItem key={district.id} value={district.name}>
                  {district.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Posta Kodu"
              value={formData.postalCode || ''}
              onChange={(e) => handleFormChange('postalCode', e.target.value)}
              disabled={!isEditingForm}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Adres"
              value={formData.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              disabled={!isEditingForm}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Eğitim Durumu"
              value={formData.educationStatus || ''}
              onChange={(e) => handleFormChange('educationStatus', e.target.value)}
              disabled={!isEditingForm}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Meslek"
              value={formData.job || ''}
              onChange={(e) => handleFormChange('job', e.target.value)}
              disabled={!isEditingForm}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Sektör"
              value={formData.sector || ''}
              onChange={(e) => handleFormChange('sector', e.target.value)}
              disabled={!isEditingForm}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Banka"
              value={formData.bank || ''}
              onChange={(e) => handleFormChange('bank', e.target.value)}
              disabled={!isEditingForm}
            >
              {banks.map((bank) => (
                <MenuItem key={bank.eft} value={bank.name}>
                  {bank.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="IBAN"
              value={formData.iban || ''}
              onChange={(e) => handleFormChange('iban', e.target.value)}
              disabled={!isEditingForm}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: formData.cvFilePath ? 2 : 0 }}>
              <SingleFileUpload
                accept="application/pdf"
                disabled={!isEditingForm}
                onUploadComplete={handleFileUpload}
                existFilePath={formData.cvFilePath}
                label="CV Yükle (PDF)"
                uploadPath="startup"
              />
            </Box>
            {formData.cvFilePath && (
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => window.open(`${process.env.REACT_APP_API_URL}/files/${formData.cvFilePath}`, '_blank')}
                sx={{ mt: 1 }}
              >
                CV Dosyasını İndir
              </Button>
            )}
          </Grid>
        </Grid>
      ) : (
        <Typography color="text.secondary">
          Girişimci Kayıt Formu Henüz Oluşturulmamış
        </Typography>
      )}
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                sx={{ 
                  width: 100, 
                  height: 100,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '2.5rem'
                }}
              >
                {user.userDetails?.userType === 'individual' 
                  ? user.individualUser?.name?.[0]
                  : user.corporateUser?.title?.[0]}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" gutterBottom>
                {user.userDetails?.userType === 'individual'
                  ? `${user.individualUser?.name} ${user.individualUser?.lastName}`
                  : user.corporateUser?.title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {getUserTypeChip()}
                <Chip
                  label={user.enabled ? 'Aktif Hesap' : 'Pasif Hesap'}
                  color={user.enabled ? 'success' : 'error'}
                  size="small"
                  icon={<Security />}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={user.enabled ? <BlockIcon /> : <UnblockIcon />}
                  onClick={() => setConfirmDialogOpen(true)}
                  sx={{
                    bgcolor: user.enabled ? theme.palette.error.main : theme.palette.success.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: user.enabled ? theme.palette.error.dark : theme.palette.success.dark,
                    },
                  }}
                >
                  {user.enabled ? 'Blokla' : 'Bloku Kaldır'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {/* User Info Section */}
          <Grid item xs={12} md={8}>
            {userInfoSection}
          </Grid>

          {/* Verification Card */}
          <Grid item xs={12} md={4}>
            {verificationCard}
          </Grid>
        </Grid>

        {/* Entrepreneur Form Section */}
        {entrepreneurFormSection}

        {/* Password Update Section */}
        <Card sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Şifre Güncelleme</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Yeni Şifre"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Yeni Şifre (Tekrar)"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleUpdatePassword}
                sx={{
                  bgcolor: theme.palette.warning.main,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.warning.dark,
                  },
                }}
              >
                Şifreyi Güncelle
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* User Activities Section */}
        <Box sx={{ mt: 4 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 3,
              color: theme.palette.primary.main,
              fontWeight: 'bold'
            }}
          >
            Kullanıcı Hareketleri
          </Typography>
          <Card>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Aktivite Tipi</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell>IP Adresi</TableCell>
                    <TableCell>Tarayıcı</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userActivities.list.map((activity) => (
                    <TableRow key={activity.userActivityId}>
                      <TableCell>{activity.activityType}</TableCell>
                      <TableCell>{formatDate(activity.registerTime)}</TableCell>
                      <TableCell>{activity.ipAddress}</TableCell>
                      <TableCell>{truncateBrowser(activity.browser)}</TableCell>
                      <TableCell>
                        <Chip
                          label={activity.success ? "Başarılı" : "Başarısız"}
                          color={activity.success ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={userActivities.count}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Sayfa başına satır:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
              }
            />
          </Card>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
          }
        }}
      >
        <DialogTitle>
          {user?.enabled ? 'Kullanıcıyı Blokla' : 'Kullanıcı Blokunu Kaldır'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {user?.enabled
              ? 'Bu kullanıcıyı bloklamak istediğinizden emin misiniz? Bloklandıktan sonra kullanıcı sisteme giriş yapamayacaktır.'
              : 'Bu kullanıcının blokunu kaldırmak istediğinizden emin misiniz? Blok kaldırıldıktan sonra kullanıcı sisteme giriş yapabilecektir.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: alpha(theme.palette.grey[500], 0.24),
              color: theme.palette.text.primary,
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleBlockUnblock}
            variant="contained"
            sx={{
              bgcolor: user?.enabled ? theme.palette.error.main : theme.palette.success.main,
              color: 'white',
              '&:hover': {
                bgcolor: user?.enabled ? theme.palette.error.dark : theme.palette.success.dark,
              },
            }}
            autoFocus
          >
            {user?.enabled ? 'Blokla' : 'Bloku Kaldır'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetailPage; 